import { Skeleton, Stack } from "@mui/material";
import { useContext, useState } from "react";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import toast from "react-hot-toast";
import { useNavigationUrl } from "../../../../hooks/useNavigationUrl";
import { ROUTES } from "../../../../constant";
import { GA_LABEL_ENUMS, triggerGAEvent } from "../../../../constant/google-analytics/GAEvents";
import { StateContext } from "../../../../context/globalContext/context";
import { iGlobalContext } from "../../../../context/globalContext/interface";
import { useHistoryContext } from "../../../../context/historyContext/historyContext";
import { iHistoryContext } from "../../../../context/historyContext/interface";
import { iSocketContext, useSocketContext } from "../../../../context/socketContext/SocketContext";
import { useTopDestinationContext } from "../../../../context/topDestinationContext/TopDestinationContext";
import { iTopDestinationContext } from "../../../../context/topDestinationContext/topDestinationContextInterface";
import { ScreenTypes } from "../../../../enums";
import { toastMessage } from "../../../../helpers/toast/toastMessage";
import useCustomErrorHandler from "../../../../hooks/useCustomErrorHandler";
import Loader from "../../loader/Loader";
import SwipeToDelete from "../../swipeToDeleteOld";
import styles from "../../../home&Landingpage/historyPage/history.module.scss";
import hstyles from "./historyOld.module.scss";
import MenuPanelPopover from "../../menuPanel/MenuPanelPopover";
import { SearchProgressStore } from "../../../../services/searchProgress/searchProgressStore";
import { SELECTED_CHAT_DATA } from "../../../../constant/dummyData";
import { rotateChatId } from "../../../../utils/chat/rotateChatId";
import { isSameConversation, resolveSelectionForChat } from "../../../../utils/chat/chatSelection";
import { getHistoryTitle, withChatTitle } from "../../../../utils/chat/chatTitle";

function HistoryTab({
  setIsOpenMobileSidebar,
  embedded = false,
}: {
  setIsOpenMobileSidebar?: any;
  isOpenMobileSidebar?: boolean;
  embedded?: boolean;
}): JSX.Element {
  const globalContext = useContext(StateContext);
  const { setChatData, isMobile, setFirstScreen, setScreen, userInfo, setChatInputText, setSelectedHotelRoomArr, chatData, selectedChatData, setSelectedChatData }: iGlobalContext =
    globalContext;
  const {
    isLoading,
    todayHistory,
    yesterdayHistory,
    oneWeekAgoHistory,
    lastOneMonth,
    historyPaginationLoaderRef,
    hasMoreHistory,
    setCurrentHistoryId,
    deleteHistoryItem,
  }: iHistoryContext = useHistoryContext();
  const { handleCustomError } = useCustomErrorHandler();
  const { navigateWithParams } = useNavigationUrl();
  const { handleCloseSocketConnection }: iSocketContext = useSocketContext();
  const { handleCancel, disableInput }: iTopDestinationContext = useTopDestinationContext();

  function handleViewHistory(data: any) {
    setChatInputText("");
    if (disableInput) {
      handleCloseSocketConnection();
      handleCancel();
    }
    setScreen("");
    setCurrentHistoryId(data?.id || "");
    // Clear any lingering room selection before restoring a saved chat — see the same guard in
    // historyPage/history.tsx. selectedHotelRoomArr persists (localStorage-backed) and isn't part
    // of the saved chat, so a stale selection would render a reopened chat's hotel as pre-selected.
    setSelectedHotelRoomArr([]);
    SearchProgressStore.reset();
    if (data?.history) setChatData(withChatTitle(data.history, data?.title));
    if (!isSameConversation(chatData, data?.history)) {
      setSelectedChatData(resolveSelectionForChat(selectedChatData, data?.history, { ...SELECTED_CHAT_DATA }));
      rotateChatId();
    }
    if (setIsOpenMobileSidebar) setIsOpenMobileSidebar(false);
    if (isMobile) navigateWithParams(ROUTES.TopDestination);
    else setFirstScreen(ScreenTypes.topDestination);

    triggerGAEvent(GA_LABEL_ENUMS.history_item_click, data.history?.[0]?.message);
  }

  async function handleDelete(data: any) {
    toast.loading("Deleting history...");
    const { success, statusCode } = await deleteHistoryItem(data);
    toast.dismiss();
    if (success) {
      toastMessage.success("SUCCESS", "History deleted!");
      triggerGAEvent(GA_LABEL_ENUMS.History_delete_click);
    } else {
      handleCustomError(statusCode, "delete.history");
    }
  }

  if (userInfo?.id === "") {
    return <></>;
  }

  if (
    isLoading &&
    todayHistory?.length === 0 &&
    oneWeekAgoHistory?.length === 0 &&
    yesterdayHistory?.length === 0 &&
    lastOneMonth?.length === 0
  )
    return embedded ? (
      <HistorySkeleton />
    ) : (
      <Stack mt={2.5}>
        <Loader white={false} />
      </Stack>
    );

  return (
    <div className={`${styles.main} ${embedded ? hstyles.embeddedMain : ""}`}>
      {!isLoading &&
      todayHistory?.length === 0 &&
      yesterdayHistory?.length === 0 &&
      oneWeekAgoHistory?.length === 0 &&
      lastOneMonth?.length === 0 ? (
        embedded ? (
          <p className={hstyles.emptyRow}>There are no previous chats.</p>
        ) : (
          <h1 className={styles.noData}>No Data</h1>
        )
      ) : (
        <div className={`${styles.scrollContainer} ${embedded ? hstyles.embeddedScroll : ""}`}>
          {embedded ? (
            <HistoryList
              historyName=""
              showTitle={false}
              list={[...todayHistory, ...yesterdayHistory, ...oneWeekAgoHistory, ...lastOneMonth]}
              handleDelete={handleDelete}
              handleViewHistory={handleViewHistory}
              isMobile={isMobile}
              embedded={embedded}
            />
          ) : (
            <>
              {todayHistory.length > 0 && (
                <HistoryList
                  historyName="Today"
                  list={todayHistory}
                  handleDelete={handleDelete}
                  handleViewHistory={handleViewHistory}
                  isMobile={isMobile}
                  embedded={embedded}
                />
              )}

              {yesterdayHistory.length > 0 && (
                <HistoryList
                  historyName="Yesterday"
                  list={yesterdayHistory}
                  handleDelete={handleDelete}
                  handleViewHistory={handleViewHistory}
                  isMobile={isMobile}
                  embedded={embedded}
                />
              )}

              {oneWeekAgoHistory.length > 0 && (
                <HistoryList
                  historyName="1 week ago"
                  list={oneWeekAgoHistory}
                  handleDelete={handleDelete}
                  handleViewHistory={handleViewHistory}
                  isMobile={isMobile}
                  embedded={embedded}
                />
              )}

              {lastOneMonth.length > 0 && (
                <HistoryList
                  historyName="Last 30 days"
                  list={lastOneMonth}
                  handleDelete={handleDelete}
                  handleViewHistory={handleViewHistory}
                  isMobile={isMobile}
                  embedded={embedded}
                />
              )}
            </>
          )}
          {hasMoreHistory && (
            <div className={styles.loader} ref={historyPaginationLoaderRef}>
              {embedded ? <HistorySkeletonRows count={2} /> : <Loader white={false} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default HistoryTab;

function HistoryList({
  list,
  handleDelete,
  handleViewHistory,
  isMobile,
  embedded,
  historyName,
  showTitle = true,
}: {
  list: any;
  handleDelete: any;
  handleViewHistory: any;
  isMobile: boolean;
  embedded?: boolean;
  historyName: string;
  showTitle?: boolean;
}) {
  return (
    <>
      <div className={styles.historyCard}>
        {showTitle && <p className={styles.title}>{historyName}</p>}
        <div className={styles.todayContainer}>
          {list.length > 0 &&
            list.map((ele: any, index: any) => {
              const previewMsg =
                getHistoryTitle(ele) ||
                (ele?.history?.find((h: any) => h?.message && !h?.shouldNotShowFirstMessage)?.message ?? "");
              if (embedded || !isMobile) {
                return (
                  <DesktopHistoryRow
                    key={index}
                    preview={previewMsg}
                    onView={() => handleViewHistory(ele)}
                    onDelete={() => handleDelete(ele)}
                  />
                );
              }
              return (
                <SwipeToDelete
                  key={index}
                  height={34}
                  onDelete={() => handleDelete(ele)}
                  onClick={() => handleViewHistory(ele)}
                >
                  <p className={styles.desc}>
                    {previewMsg.length > 35 ? previewMsg.substring(0, 35) + " ..." : previewMsg}
                  </p>
                </SwipeToDelete>
              );
            })}
        </div>
      </div>
      {showTitle && <div className={styles.divider} />}
    </>
  );
}

function DesktopHistoryRow({
  preview,
  onView,
  onDelete,
}: {
  preview: string;
  onView: () => void;
  onDelete: () => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  return (
    <div className={`${hstyles.hoverRow} ${open ? hstyles.active : ""}`}>
      <button type="button" className={hstyles.hoverRowMain} onClick={onView}>
        <span className={hstyles.hoverRowText}>{preview}</span>
      </button>
      <button
        type="button"
        className={hstyles.hoverMoreBtn}
        aria-label="History options"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      >
        <MoreHorizIcon sx={{ fontSize: 16 }} />
      </button>
      <MenuPanelPopover
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        width="hug"
        placement="right-start"
        items={[
          {
            icon: <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />,
            label: "Remove",
            onClick: onDelete,
          },
        ]}
      />
    </div>
  );
}

function HistorySkeletonRows({ count }: { count: number }) {
  const widths = ["88%", "72%", "94%", "63%", "80%"];
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={hstyles.skeletonRow}>
          <Skeleton variant="rounded" width={widths[i % widths.length]} height={16} sx={{ borderRadius: "6px" }} />
        </div>
      ))}
    </>
  );
}

function HistorySkeleton() {
  return (
    <div className={hstyles.skeletonWrap}>
      {[0, 1].map((group) => (
        <div key={group} className={hstyles.skeletonGroup}>
          <Skeleton
            variant="rounded"
            width={64}
            height={12}
            sx={{ borderRadius: "4px", marginLeft: "12px", marginBottom: "4px" }}
          />
          <HistorySkeletonRows count={4} />
        </div>
      ))}
    </div>
  );
}
